import * as React from "react";
import { Alert, Button, Modal } from "antd";
import { MinusCircleOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { DomainPortInput } from "../../schema/types/domain/input";
import { formSetValue, useCpFormValue } from "../gvc/testNewForm/utils";
import { store } from "../../store";
import { get } from "lodash";
import { CpInputNumber } from "../gvc/testNewForm/CpInputNumber";
import { CpKindSelect } from "../gvc/testNewForm/temp/CpKindSelect";
import { useCpFormContext } from "../../components/form/useCpFormContext";
import { parseLink } from "../../utils/linkParser/linkParser";
import { getDefaultKindSelectState } from "../../components/antd/kindselect/KindSelect";
import { WorkloadGVCTagFn } from "../../components/antd/kindselect/tagRenderers/workloadGvcTag";

interface Props {
  portIndex: number;
  onClose: () => void;
}
export const PortRouteEditMirrorsModal: React.FC<Props> = ({ portIndex, onClose }) => {
  const { id } = useCpFormContext();

  const port: DomainPortInput = useCpFormValue(`advanced.spec.ports.${portIndex}`);

  return (
    <Modal
      open={true}
      styles={{ body: { height: "70vh", padding: 0 } }}
      width={800}
      title={`Edit Route Mirrors for Port ${port.number}`}
      onCancel={onClose}
      maskClosable={false}
      footer={
        <div className="flex items-center justify-end gap-4">
          <Button
            type="primary"
            onClick={() => {
              onClose();
            }}
          >
            OK
          </Button>
        </div>
      }
    >
      <div className="h-full overflow-auto">
        <Alert
          showIcon
          type="info"
          className="mb-4 mx-4 mt-4"
          message="Mirror traffic to the specified workload(s). Only works for workloads running in the same location as the primary workload(s)."
        />
        {port.routes.map((route, routeIndex) => {
          const workloadName = parseLink(route.workload.link, { useInputCtx: true }).name;

          return (
            <div key={route.id}>
              <div className="mb-8 px-4">
                <div className="flex items-center gap-4 mb-2">
                  <div className="cursor-default">{`Workload: ${workloadName} ${
                    route.prefixType === "prefix" ? `Prefix: ${route.prefix}` : `Regex: ${route.regex}`
                  }`}</div>
                  <Button
                    type="primary"
                    size={"small"}
                    onClick={() => {
                      const form = store.getState().form.instances[id];
                      const values = form.values;
                      const mirrors =
                        get(values, `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror`) || [];
                      formSetValue(
                        id,
                        `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror` as const,
                        [
                          ...mirrors,
                          {
                            id: uuidv4(),
                            workload: getDefaultKindSelectState(),
                            percent: "",
                            port: "",
                          },
                        ],
                      );
                    }}
                  >
                    Add
                  </Button>
                </div>
                {(route.mirror || []).length < 1 ? <div>No Mirror Targets</div> : null}
                {(route.mirror || []).length > 0 ? (
                  <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: "1fr 120px 80px 32px" }}>
                    <div className="text-xs color-label">Workload</div>
                    <div className="text-xs color-label">Percent</div>
                    <div className="text-xs color-label">Port</div>
                    <div />
                  </div>
                ) : null}
                {(route.mirror || []).map((mirror, mirrorIndex) => (
                  <div
                    key={mirror.id}
                    className="grid gap-2 mb-1 items-start"
                    style={{ gridTemplateColumns: "1fr 120px 80px 32px" }}
                  >
                    <CpKindSelect
                      name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.workload`}
                      optionValue="relative"
                      kind="workload"
                      tagRenderers={[WorkloadGVCTagFn(false)]}
                    />
                    <CpInputNumber
                      name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.percent`}
                      className="free-width w-full"
                    />
                    <CpInputNumber
                      name={`advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror.${mirrorIndex}.port`}
                      className="free-width w-full"
                    />
                    <MinusCircleOutlined
                      className="color-danger"
                      style={{ marginTop: 6 }}
                      onClick={() => {
                        const form = store.getState().form.instances[id];
                        const values = form.values;
                        const mirrors =
                          get(values, `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror`) || [];
                        const newMirrors = mirrors.filter((_, idx) => idx !== mirrorIndex);
                        formSetValue(
                          id,
                          `advanced.spec.ports.${portIndex}.routes.${routeIndex}.mirror` as const,
                          newMirrors,
                        );
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
};
